import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Modal, Button, Spinner, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import debounce from "lodash/debounce";
import closeIcon from "../../../common/assets/images/x-close.png";

// Import Redux actions
import {
  updateCodingSupportFilters,
  resetCodingSupportFilters,
  getSpecialityRecommendations,
  getMedicalRecordNumberRecommendations,
  clearSpecialityRecommendations,
  clearMedicalRecordNumberRecommendations,
  fetchCodingSupport,
  getCodingSupportProvisionalDrgData,
  setQueueFilterSearch,
  getQueueFilterOptions,
  fetchCodingSupportcards,
} from "./Redux/codingSupportSlicer";

import "./CodingSupportModal.css";
import {
  getEpisodeIdentiferFilterOptions,
  getHealthFundContractFilterOptions,
  getMRNFilterOptions,
  getPriorityFilterOptions,
  getProvisionalDrgFilterOptions,
  getSpecialityFilterOptions,
  getStatusFilterOptions,
  resetFilterByKey,
  setEpisodeIdentiferFilterSearch,
  setHealthFundFilterContractSearch,
  setMRNFilterSearch,
  setPriorityFilterSearch,
  setProvisionalDrgFilterSearch,
  setSpecialityFilterSearch,
  setStatusFilterSearch,
} from "../HealthInsuranceManager/redux/HealthInformationManagerSlicer";
import MultiSelect from "../../../utils/MultiSelect";

const CodingSupportModal = ({
  show,
  setShowFilterModal,
  onHide,
  selectedQueue,
  setCoderSearchTerm,
}) => {
  const dispatch = useDispatch();
  const {
    provisionalDrgData,
    provisionalDrgLoading,
    isprovisionalDrgPageTrue,
    provisionalDrgPageNumber,
    provisionalDrgSearch,
  } = useSelector(
    (state) => state.codingSupport.provisionalDrgCodingSupportOptions,
  );
  const labelCache = useRef({
    speciality: {},
    queue: {},
    status: {},
  });
  const {
    episodeIdentiferFilterOptions,
    MRNFilterOptions,
    specialityFilterOptions,
    coderFilterOptions,
    priorityFilterOptions,
    statusFilterOptions,
    healthFundContractFilterOptions,
    provisionalDrgFilterOptions,
  } = useSelector((state) => state.healthInsuranceManager);

  const missingNotesOptions = [
    { label: "Discharge Summary", value: "Discharge Summary" },
    { label: "Operation Report", value: "Operation Report" },
    { label: "Histopathology", value: "Histopathology" },
    { label: "Progress Note()", value: "Progress Note()" },
    { label: "HITH Note()", value: "HITH Note()" },
    { label: "Anaesthetic Chart", value: "Anaesthetic Chart" },
    { label: "Medication Chart", value: "Medication Chart" },
    { label: "Cardiac Cathlab report", value: "Cardiac Cathlab report" },
    { label: "Sleep Study Report", value: "Sleep Study Report" },
    { label: "Incorrect Admission Date", value: "Incorrect Admission Date" },
    { label: "Incorrect Discharge Date", value: "Incorrect Discharge Date" },
    { label: "Other (specify)", value: "Other (specify)" },
  ];

  useEffect(() => {
    if (show) {
      const updatedPayload = {
        ...filtersPayload,
        queue: [selectedQueue?.queue_id],
      };
      dispatch(fetchCodingSupport(updatedPayload));
      dispatch(fetchCodingSupportcards(updatedPayload));

      dispatch(
        getEpisodeIdentiferFilterOptions({
          search: episodeIdentiferFilterOptions.search,
          page_number: 1,
          page_size: 10,
        }),
      );
      dispatch(
        getMRNFilterOptions({
          search: MRNFilterOptions.search,
          page_number: 1,
          page_size: 10,
        }),
      );
      dispatch(
        getSpecialityFilterOptions({
          search: specialityFilterOptions.search,
          page_number: 1,
          page_size: 10,
        }),
      );
      dispatch(
        getQueueFilterOptions({
          search: queueFilterOptions.search,
          page_number: 1,
          page_size: 10,
        }),
      );
      dispatch(
        getPriorityFilterOptions({
          search: priorityFilterOptions.search,
          page_number: 1,
          page_size: 10,
        }),
      );
      dispatch(
        getStatusFilterOptions({
          search: statusFilterOptions.search,
          page_number: 1,
          page_size: 10,
        }),
      );
      dispatch(
        getHealthFundContractFilterOptions({
          search: healthFundContractFilterOptions.search,
          page_number: 1,
          page_size: 10,
        }),
      );
      dispatch(
        getProvisionalDrgFilterOptions({
          search: provisionalDrgFilterOptions.search,
          page_number: 1,
          page_size: 10,
        }),
      );
    }
  }, [show, dispatch]);

  // Redux Selectors
  const filtersPayload = useSelector(
    (state) => state.codingSupport.codingSupportPayload,
  );
  const queueFilterOptions = useSelector(
    (state) => state.codingSupport.queueFilterOptions,
  );

  const losOperatorOptions = [
    { label: ">", value: "gt" },
    { label: "<", value: "lt" },
    { label: ">=", value: "gte" },
    { label: "<=", value: "lte" },
    { label: "=", value: "eq" },
    { label: "!=", value: "neq" },
    { label: "Between", value: "gtlt" },
    { label: "Between (Inclusive)", value: "gtelte" },
  ];

  const losOpLabelMap = {
    gt: ">",
    lt: "<",
    gte: ">=",
    lte: "<=",
    eq: "=",
    neq: "!=",
    gtlt: "Between",
    gtelte: "Between (Inclusive)",
  };

  const formatYYYYMMDD = (d) =>
    d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0",
        )}-${String(d.getDate()).padStart(2, "0")}`
      : null;

  //Bread Crumb
  const formatDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const transformLosForApi = (operator, value, toValue = "") => {
    const los = [];

    if (!operator || value === "" || value === undefined) return [];

    const losValue =
      value === "no_los" || value === null ? "No_los" : Number(value);

    const isRange = operator === "gtlt" || operator === "gtelte";

    if (isRange) {
      if (toValue === "") return [];
      const losToValue =
        toValue === "no_los" || toValue === null ? "No_los" : Number(toValue);
      los.push(losValue, losToValue);
    } else {
      los.push(losValue);
    }
    return los;
  };

  const handleApplyFilters = () => {
        const payload = {
      ...filtersPayload,
      los: transformLosForApi(
        filtersPayload.los_op,
        filtersPayload.los?.[0],
        filtersPayload.los?.[1],
      ),
      page_number: 1,
    };
    // This function needs to be implemented to fetch data with filters
    dispatch(fetchCodingSupport({ ...payload, page_number: 1 }));
    dispatch(fetchCodingSupportcards({ ...payload, page_number: 1 }));
    setShowFilterModal(false);
    dispatch(updateCodingSupportFilters({ ...payload, page_number: 1 }));
    setCoderSearchTerm("");
    // onHide();
  };

  const handleResetFilters = () => {
    dispatch(resetCodingSupportFilters());
  };

  // Helper to remove individual filter tags
  const removeFilter = (key, value) => {
    let newValue;
    if (Array.isArray(filtersPayload[key])) {
      newValue = filtersPayload[key].filter((item) => item !== value);
    } else {
      newValue = "";
    }
    dispatch(updateCodingSupportFilters({ [key]: newValue }));
  };

  // Handle LOS input change (store as number)
  const handleLOSInputChange = (inputValue) => {
    if (inputValue === "") {
      // Clear if empty
      dispatch(updateCodingSupportFilters({ los: [] }));
      return;
    }

    // Convert to number
    const numValue = parseInt(inputValue);

    // Only allow numbers starting from 1
    if (!isNaN(numValue) && numValue >= 1) {
      // Store as array with single number value [1]
      dispatch(updateCodingSupportFilters({ los: [numValue] }));
    }
  };

  // Handle LOS input blur - validate the input
  const handleLOSInputBlur = (e) => {
    const inputValue = e.target.value.trim();

    if (!inputValue) {
      dispatch(updateCodingSupportFilters({ los: [] }));
      return;
    }

    // Ensure it's a valid number >= 1
    const numValue = parseInt(inputValue);
    if (!isNaN(numValue) && numValue >= 1) {
      // Store as number [1]
      dispatch(updateCodingSupportFilters({ los: [numValue] }));
    } else {
      // If invalid, clear it
      dispatch(updateCodingSupportFilters({ los: [] }));
      e.target.value = "";
    }
  };

  const getLosBreadcrumbText = (operator, losArray) => {
    if (
      !operator ||
      !Array.isArray(losArray) ||
      !losArray.some((v) => typeof v === "number" && !isNaN(v))
    ) {
      return null;
    }

    const opText = losOpLabelMap[operator] || operator;

    // Single value
    if (losArray.length === 1) {
      return `${opText} ${losArray[0]} days`;
    }

    // Range
    if (losArray.length === 2) {
      return `${opText} ${losArray[0]} and ${losArray[1]} days`;
    }

    return null;
  };

  const displayKeyMap = {
    episode_identifier_search: "Episode Identifier",
    medical_record_number: "MRN/UR",
    speciality: "Speciality",
    queue: "Queue",
    status: "Status",
    priority: "Priority",
    health_fund_contract: "Health Fund Contract",
    drg_code: "Provisional DRG",
  };

  const getLabel = (key, value) => {
    // map keys for lookup
    const mappedKey =
      key === "status"
        ? "status"
        : key === "speciality"
          ? "speciality"
          : key === "queue"
            ? "queue"
              : key;

    // date format
    if (
      key === "separation_date_from" ||
      key === "separation_date_to" ||
      key === "admission_date_from" ||
      key === "admission_date_to"
    ) {
      const [y, m, d] = value.split("-");
      return `${d}/${m}/${y}`;
    }

    // label lookup
    const option = labelMaps[mappedKey]?.find((o) => o.value === value);
    return option ? option.label : value;
  };

  const episodeIdentiferFieldOptions = useMemo(() => {
    const apiOptions = (episodeIdentiferFilterOptions.data || []).map(
      (item) => ({
        label: item,
        value: item,
      }),
    );

    const episode_identifier_search = [].concat(
      filtersPayload?.episode_identifier_search || [],
    );

    const selectedOptions = episode_identifier_search.map((value) => ({
      label: value,
      value: value,
    }));

    const map = new Map();

    [...selectedOptions, ...apiOptions].forEach((item) => {
      map.set(item.value, item);
    });

    return Array.from(map.values());
  }, [
    episodeIdentiferFilterOptions.data,
    filtersPayload?.episode_identifier_search,
  ]);

  const MRNFieldOptions = useMemo(() => {
    const apiOptions = (MRNFilterOptions.data || []).map((item) => ({
      label: item,
      value: item,
    }));

    const medical_record_number = [].concat(
      filtersPayload?.medical_record_number || [],
    );

    const selectedOptions = medical_record_number.map((value) => ({
      label: value,
      value: value,
    }));

    const map = new Map();

    [...selectedOptions, ...apiOptions].forEach((item) => {
      map.set(item.value, item);
    });

    return Array.from(map.values());
  }, [MRNFilterOptions.data, filtersPayload?.medical_record_number]);

  const specialityFieldOptions = useMemo(() => {
    const apiOptions = (specialityFilterOptions?.data || []).map((item) => {
      labelCache.current.speciality[item.hospital_speciality_id] =
        item.speciality;

      return {
        label: item.speciality,
        value: item.hospital_speciality_id,
      };
    });

    const selectedOptions = (filtersPayload?.speciality || []).map((value) => ({
      value,
      label: labelCache.current.speciality[value] || value,
    }));

    const map = new Map();
    [...selectedOptions, ...apiOptions].forEach((item) => {
      map.set(item.value, item);
    });

    return Array.from(map.values());
  }, [specialityFilterOptions?.data, filtersPayload?.speciality]);

  const queueFieldOptions = useMemo(() => {
      const apiOptions = (queueFilterOptions.data || []).map((item) => {
        labelCache.current.queue[item.queue_id] = item.queue_name;
  
        return {
          label: item.queue_name,
          value: item.queue_id,
        };
      });
  
      const selectedOptions = (filtersPayload.queue || []).map((value) => ({
        value,
        label: labelCache.current.queue[value] || value,
      }));
  
      const map = new Map();
      [...selectedOptions, ...apiOptions].forEach((item) => {
        map.set(item.value, item);
      });
  
      return Array.from(map.values());
    }, [queueFilterOptions.data, filtersPayload.queue]);

  const priorityFiledOptions = useMemo(() => {
    const apiOptions = priorityFilterOptions.data.map((item) => ({
      label: item,
      value: item,
    }));

    const selectedOptions = filtersPayload?.priority?.map((value) => ({
      label: value,
      value: value,
    }));

    const map = new Map();

    [...selectedOptions, ...apiOptions].forEach((item) => {
      map.set(item.value, item);
    });

    return Array.from(map.values());
  }, [priorityFilterOptions.data, filtersPayload?.priority]);


  const statusFiledOptions = useMemo(() => {
      const apiOptions = (statusFilterOptions.data || []).map((item) => {
        labelCache.current.status[item.status_id] = item.status;
  
        return {
          label: item.status,
          value: item.status_id,
        };
      });
  
      const selectedOptions = (filtersPayload?.status || []).map((value) => ({
        value,
        label: labelCache.current.status[value] || value,
      }));
  
      const map = new Map();
  
      [...selectedOptions, ...apiOptions].forEach((item) => {
        map.set(item.value, item);
      });
  
      return Array.from(map.values());
    }, [statusFilterOptions.data, filtersPayload?.status]);
  
  const healthFundFieldOptions = useMemo(() => {
    const apiOptions = healthFundContractFilterOptions.data.map((item) => ({
      label: item,
      value: item,
    }));

    const selectedOptions = filtersPayload?.health_fund_contract?.map(
      (value) => ({
        label: value,
        value: value,
      }),
    );

    const map = new Map();

    [...selectedOptions, ...apiOptions].forEach((item) => {
      map.set(item.value, item);
    });

    return Array.from(map.values());
  }, [
    healthFundContractFilterOptions.data,
    filtersPayload?.health_fund_contract,
  ]);
  const provisionalDrgFieldOptions = useMemo(() => {
    const apiOptions = provisionalDrgFilterOptions.data.map((item) => ({
      label: item,
      value: item,
    }));

    const selectedOptions = filtersPayload?.drg_code?.map((value) => ({
      label: value,
      value: value,
    }));

    const map = new Map();

    [...selectedOptions, ...apiOptions].forEach((item) => {
      map.set(item.value, item);
    });

    return Array.from(map.values());
  }, [provisionalDrgFilterOptions.data, filtersPayload?.drg_code]);

  const labelMaps = {
    episode_identifier_search: episodeIdentiferFieldOptions,
    medical_record_number: MRNFieldOptions,
    speciality: specialityFieldOptions,
    queue: queueFieldOptions,
    status: statusFiledOptions,
    priority: priorityFiledOptions,
    health_fund_contract: healthFundFieldOptions,
    los_op: losOperatorOptions,
    drg_code: provisionalDrgFieldOptions,
  };

  const breadcrumbExcludeKeys = ["page_number", "page_size"];

  const hasValue = Object.entries(filtersPayload).some(
    ([key, val]) =>
      !breadcrumbExcludeKeys.includes(key) &&
      (Array.isArray(val) ? val.length > 0 : !!val),
  );

  // React Select menu props for modal
  const selectMenuProps = {
    menuPortalTarget: document.body,
    menuPosition: "fixed",
    styles: {
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
    },
  };

  const debouncedSearchRef = useRef(
    debounce((thunk, value) => {
      dispatch(
        thunk({
          search: value || "",
          page_number: 1,
          page_size: 10,
        }),
      );
    }, 1000),
  );

  const handleInputChange =
    (setSearchAction, fetchThunk) =>
    (value, { action }) => {
      if (action !== "input-change") {
        setSearchAction("");
      }

      dispatch(setSearchAction(value));
      debouncedSearchRef.current(fetchThunk, value);
    };

  const handleScrollBottom = (options, thunk) => () => {
    if (options.hasMore && !options.loading) {
      dispatch(
        thunk({
          search: options.search || "",
          page_number: options.pageNumber + 1,
          page_size: 10,
        }),
      );
    }
  };

  const handleOnFocus = (optionsState, fetchThunk) => {
    if (optionsState?.data?.length === 0 && !optionsState.loading) {
      dispatch(
        fetchThunk({
          search: "",
          page_number: 1,
          page_size: 10,
        }),
      );
    }
  };

  const handleOnChange = async ({
    key,
    selected,
    optionsState,
    fetchThunk,
    searchResetAction,
    minVisible = 4,
  }) => {
    const selectedArray = selected || [];

    const selectedValues =
      key === "speciality" || key === "queue" || key === "status"
        ? selectedArray.map((s) => s.value)
        : selectedArray.map((s) => s.label);

    dispatch(updateCodingSupportFilters({ [key]: selectedValues }));

    if (optionsState.search) {
      dispatch(searchResetAction(""));
      debouncedSearchRef.current?.cancel?.();

      let currentPage = 1;
      let hasMore = true;
      let accumulatedData = [];

      while (hasMore) {
        const response = await dispatch(
          fetchThunk({
            search: "",
            page_number: currentPage,
            page_size: 10,
          }),
        ).unwrap();

        const dataMap = {
          episode_identifier_search: response.episode_identifier_number,
          medical_record_number: response.medical_record_number,
          speciality: response?.specialities,
          queue: response?.queues?.data,
          coders: response?.users,
          priority: response?.priority,
          status: response?.queues?.status,
          health_funds: response?.queues?.specialities,
          drg_code: response?.queues?.drg_codes,
        };

        const pageData = dataMap[key] || [];
        accumulatedData = [...accumulatedData, ...pageData];
        hasMore = response.has_more;
        const visibleOptions = accumulatedData.filter((item) => {
          const compareValue =
            key === "queue" || key === "coders" ? item.value : item;

          return !selectedValues.includes(compareValue);
        });

        if (visibleOptions.length >= minVisible) break;
        if (!hasMore) break;

        currentPage += 1;
      }
      return;
    }

    const visibleOptions = optionsState.data.filter((item) => {
      let compareValue;

      if (typeof item === "object") {
        if (key === "speciality") {
          compareValue = item.hospital_speciality_id;
        } else if (key === "queue") {
          compareValue = item.queue_id;
        } else if (key === "status") {
          compareValue = item.status_id;
        } else {
          compareValue = item.value || item.label;
        }
      } else {
        compareValue = item;
      }

      return !selectedValues.includes(compareValue);
    });

    if (
      visibleOptions.length < minVisible &&
      optionsState.hasMore &&
      !optionsState.loading
    ) {
      dispatch(
        fetchThunk({
          search: "",
          page_number: optionsState.pageNumber + 1,
          page_size: 10,
        }),
      );
    }
  };

  const handleResetOnBlur = (stateKey, searchResetAction) => () => {
    debouncedSearchRef.current?.cancel?.();
    dispatch(searchResetAction(""));
    dispatch(resetFilterByKey(stateKey));
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      dialogClassName="modal-xl"
    >
      <Modal.Header closeButton className="cs-modal-header">
        <Modal.Title className="cs-modal-title">
          Missing Notes Filters
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Active Filter Chips - Top Breadcrumb Section */}
        {hasValue && (
          <div className="episodelist-filter-breadcrumb-section-him">
            <div className="episodelist-breadcrumb-list-him">
              {Object.entries(filtersPayload)
                .filter(([key]) => !breadcrumbExcludeKeys.includes(key))
                .map(([key, value]) => {
                  if (!value || (Array.isArray(value) && value.length === 0))
                    return null;

                  if (key === "los") return null;

                  if (key === "los_op") {
                    const losText = getLosBreadcrumbText(
                      filtersPayload.los_op,
                      filtersPayload.los,
                    );
                    if (!losText) return null;

                    return (
                      <Badge
                        key="los"
                        bg="light"
                        text="dark"
                        className="episodeListFilterObj-breadcrumbItem-him"
                      >
                        LOS : {losText}
                        <img
                          src={closeIcon}
                          className="episodeListFilterObj-close-icon-him"
                          onClick={() =>
                            dispatch(
                              updateCodingSupportFilters({
                                los_op: "",
                                los: [],
                              }),
                            )
                          }
                        />
                      </Badge>
                    );
                  }

                  // Date fields
                  if (
                    key === "separation_date_from" ||
                    key === "separation_date_to" ||
                    key === "admission_date_from" ||
                    key === "admission_date_to"
                  ) {
                    const [y, m, d] = value.split("-");
                    return (
                      <Badge
                        key={key}
                        bg="light"
                        text="dark"
                        className="episodeListFilterObj-breadcrumbItem-him"
                      >
                        {/* {key.replace(/_/g, " ")} : {`${d}/${m}/${y}`} */}
                        {displayKeyMap[key] || key.replace(/_/g, " ")} :{" "}
                        {getLabel(key, value)}
                        <img
                          src={closeIcon}
                          className="episodeListFilterObj-close-icon-him"
                          onClick={() =>
                            dispatch(
                              updateCodingSupportFilters({ [key]: null }),
                            )
                          }
                        />
                      </Badge>
                    );
                  }

                  //  Array filters → one badge per value
                  // if (Array.isArray(value)) {
                  //   return value.map((item) => (
                  //     <Badge
                  //       key={`${key}-${item}`}
                  //       bg="light"
                  //       text="dark"
                  //       className="episodeListFilterObj-breadcrumbItem-him"
                  //     >
                  //       {/* {key.replace(/_/g, " ")} : {getLabel(key, item)} */}
                  //       {displayKeyMap[key] || key.replace(/_/g, " ")} : {getLabel(key, value)}
                  //       <img
                  //         src={closeIcon}
                  //         className="episodeListFilterObj-close-icon-him"
                  //         onClick={() =>
                  //           dispatch(
                  //             updateCodingSupportFilters({
                  //               [key]: value.filter((v) => v !== item),
                  //             }),
                  //           )
                  //         }
                  //       />
                  //     </Badge>
                  //   ));
                  // }
                  if (Array.isArray(value)) {
                    return value.map((item) => (
                      <Badge
                        key={`${key}-${item}`}
                        bg="light"
                        text="dark"
                        className="episodeListFilterObj-breadcrumbItem-him"
                      >
                        {displayKeyMap[key] || key.replace(/_/g, " ")} :{" "}
                        {getLabel(key, item)}
                        <img
                          src={closeIcon}
                          className="episodeListFilterObj-close-icon-him"
                          onClick={() =>
                            dispatch(
                              updateCodingSupportFilters({
                                [key]: value.filter((v) => v !== item),
                              }),
                            )
                          }
                        />
                      </Badge>
                    ));
                  }

                  // 🔹 Single-value filters
                  return (
                    <Badge
                      key={key}
                      bg="light"
                      text="dark"
                      className="episodeListFilterObj-breadcrumbItem-him"
                    >
                      {/* {key.replace(/_/g, " ")} : {getLabel(key, value)} */}
                      {displayKeyMap[key] || key.replace(/_/g, " ")} :{" "}
                      {getLabel(key, value)}
                      <img
                        src={closeIcon}
                        className="episodeListFilterObj-close-icon-him"
                        onClick={() =>
                          dispatch(updateCodingSupportFilters({ [key]: "" }))
                        }
                      />
                    </Badge>
                  );
                })}
            </div>

            <Button
              className="episodelist-reset-btn"
              onClick={handleResetFilters}
            >
              Clear All
            </Button>
          </div>
        )}

        <div className="cs-filter-grid">
          {/* Episode ID */}
          <div className="cs-filter-field">
            <label className="cs-filter-label">Episode Identifier</label>
            <MultiSelect
              isMulti={true}
              options={episodeIdentiferFieldOptions}
              isLoading={episodeIdentiferFilterOptions.loading}
              hasMore={episodeIdentiferFilterOptions.hasMore}
              placeholder="Search episode identifer"
              onScrollBottom={handleScrollBottom(
                episodeIdentiferFilterOptions,
                getEpisodeIdentiferFilterOptions,
              )}
              onInputChange={handleInputChange(
                setEpisodeIdentiferFilterSearch,
                getEpisodeIdentiferFilterOptions,
              )}
              onFocus={() => {
                handleOnFocus(
                  episodeIdentiferFilterOptions,
                  getEpisodeIdentiferFilterOptions,
                );
              }}
              onMenuClose={handleResetOnBlur(
                "episodeIdentiferFilterOptions",
                setEpisodeIdentiferFilterSearch,
              )}
              onChange={(selected) => {
                handleOnChange({
                  key: "episode_identifier_search",
                  selected,
                  optionsState: episodeIdentiferFilterOptions,
                  fetchThunk: getEpisodeIdentiferFilterOptions,
                  searchResetAction: setEpisodeIdentiferFilterSearch,
                });
              }}
              value={episodeIdentiferFieldOptions?.filter((o) =>
                filtersPayload.episode_identifier_search?.includes(o?.value),
              )}
            />
          </div>
          {/* MRN */}
          <div className="cs-filter-field">
            <label className="cs-filter-label">MRN/UR</label>
            <MultiSelect
              isMulti={true}
              options={MRNFieldOptions}
              isLoading={MRNFilterOptions.loading}
              hasMore={MRNFilterOptions.hasMore}
              placeholder="Search MRN"
              onScrollBottom={handleScrollBottom(
                MRNFilterOptions,
                getMRNFilterOptions,
              )}
              onInputChange={handleInputChange(
                setMRNFilterSearch,
                getMRNFilterOptions,
              )}
              onFocus={() => {
                handleOnFocus(MRNFilterOptions, getMRNFilterOptions);
              }}
              onMenuClose={handleResetOnBlur(
                "MRNFilterOptions",
                setMRNFilterSearch,
              )}
              onChange={(selected) => {
                handleOnChange({
                  key: "medical_record_number",
                  selected,
                  optionsState: MRNFilterOptions,
                  fetchThunk: getMRNFilterOptions,
                  searchResetAction: setMRNFilterSearch,
                });
              }}
              value={MRNFieldOptions.filter((o) =>
                filtersPayload.medical_record_number.includes(o.value),
              )}
            />
          </div>
          {/* Admission from           */}
          <div className="cs-filter-field">
            <label className="cs-filter-label">Admission Date From</label>
            <DatePicker
              selected={
                filtersPayload.admission_date_from
                  ? new Date(filtersPayload.admission_date_from)
                  : null
              }
              onChange={(date) =>
                dispatch(
                  updateCodingSupportFilters({
                    admission_date_from: formatYYYYMMDD(date),
                    admission_date_to:
                      !date ||
                      (filtersPayload.admission_date_to &&
                        new Date(filtersPayload.admission_date_to) < date)
                        ? null
                        : filtersPayload.admission_date_to,
                  }),
                )
              }
              className="cs-filter-date-picker"
              dateFormat="dd/MM/yyyy"
              placeholderText="Admission Date From"
              showMonthDropdown
              showYearDropdown
              maxDate={new Date()}
              dropdownMode="select"
              onKeyDown={(e) => e.key !== "Backspace" && e.preventDefault()}
            />
          </div>
          {/* Admission to           */}
          <div className="cs-filter-field">
            <label className="cs-filter-label">Admission Date To</label>
            <DatePicker
              selected={
                filtersPayload.admission_date_to &&
                filtersPayload.admission_date_from
                  ? new Date(filtersPayload.admission_date_to)
                  : null
              }
              onChange={(date) =>
                dispatch(
                  updateCodingSupportFilters({
                    admission_date_to: formatYYYYMMDD(date),
                  }),
                )
              }
              className="cs-filter-date-picker"
              dateFormat="dd/MM/yyyy"
              placeholderText="Admission Date To"
              disabled={!filtersPayload.admission_date_from}
              showMonthDropdown
              showYearDropdown
              maxDate={new Date()}
              dropdownMode="select"
              minDate={filtersPayload?.admission_date_from ?? null}
              onKeyDown={(e) => e.key !== "Backspace" && e.preventDefault()}
            />
          </div>

          {/* From Date */}
          <div className="cs-filter-field">
            <label className="cs-filter-label">Separation Date From</label>
            <DatePicker
              selected={
                filtersPayload.separation_date_from
                  ? new Date(filtersPayload.separation_date_from)
                  : null
              }
              onChange={(date) =>
                dispatch(
                  updateCodingSupportFilters({
                    separation_date_from: formatYYYYMMDD(date),
                    separation_date_to:
                      !date ||
                      (filtersPayload.separation_date_to &&
                        new Date(filtersPayload.separation_date_to) < date)
                        ? null
                        : filtersPayload.separation_date_to,
                  }),
                )
              }
              className="cs-filter-date-picker"
              dateFormat="dd/MM/yyyy"
              placeholderText="Separation Date From"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              maxDate={new Date()}
              minDate={
                filtersPayload?.admission_date_from ??
                filtersPayload?.admission_date_from
              }
              onKeyDown={(e) => e.key !== "Backspace" && e.preventDefault()}
            />
          </div>

          {/* To Date */}
          <div className="cs-filter-field">
            <label className="cs-filter-label">Separation Date To</label>
            <DatePicker
              selected={
                filtersPayload.separation_date_to &&
                filtersPayload.separation_date_from
                  ? new Date(filtersPayload.separation_date_to)
                  : null
              }
              onChange={(date) =>
                dispatch(
                  updateCodingSupportFilters({
                    separation_date_to: formatYYYYMMDD(date),
                  }),
                )
              }
              className="cs-filter-date-picker"
              dateFormat="dd/MM/yyyy"
              placeholderText="Separation Date To"
              disabled={!filtersPayload.separation_date_from}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              minDate={filtersPayload?.separation_date_from}
              maxDate={new Date()}
              onKeyDown={(e) => e.key !== "Backspace" && e.preventDefault()}
            />
          </div>

          {/* Speciality */}
          <div className="cs-filter-field">
            <label className="cs-filter-label">Speciality</label>

            <MultiSelect
              isMulti={true}
              options={specialityFieldOptions}
              isLoading={specialityFilterOptions.loading}
              hasMore={specialityFilterOptions.hasMore}
              placeholder="Search speciality"
              onScrollBottom={handleScrollBottom(
                specialityFilterOptions,
                getSpecialityFilterOptions,
              )}
              onInputChange={handleInputChange(
                setSpecialityFilterSearch,
                getSpecialityFilterOptions,
              )}
              onFocus={() => {
                handleOnFocus(
                  specialityFilterOptions,
                  getSpecialityFilterOptions,
                );
              }}
              onBlur={handleResetOnBlur(
                "specialityFilterOptions",
                setSpecialityFilterSearch,
              )}
              onChange={(selected) => {
                handleOnChange({
                  key: "speciality",
                  selected,
                  optionsState: specialityFilterOptions,
                  fetchThunk: getSpecialityFilterOptions,
                  searchResetAction: setSpecialityFilterSearch,
                });
              }}
              value={specialityFieldOptions.filter((o) =>
                filtersPayload.speciality.includes(o.value),
              )}
            />
          </div>

          {/* Queue */}
          <div className="cs-filter-field">
            <label className="cs-filter-label">Queue</label>
            <MultiSelect
              isMulti={true}
              options={queueFieldOptions}
              isLoading={queueFilterOptions.loading}
              hasMore={queueFilterOptions.hasMore}
              placeholder="Search Queue"
              onScrollBottom={handleScrollBottom(
                queueFilterOptions,
                getQueueFilterOptions,
              )}
              onInputChange={handleInputChange(
                setQueueFilterSearch,
                getQueueFilterOptions,
              )}
              onFocus={() => {
                handleOnFocus(queueFilterOptions, getQueueFilterOptions);
              }}
              onMenuClose={handleResetOnBlur(
                "queueFilterOptions",
                setQueueFilterSearch,
              )}
              onChange={(selected) => {
                handleOnChange({
                  key: "queue",
                  selected,
                  optionsState: queueFilterOptions,
                  fetchThunk: getQueueFilterOptions,
                  searchResetAction: setQueueFilterSearch,
                });
              }}
              value={queueFieldOptions.filter((o) =>
                filtersPayload.queue.includes(o.value),
              )}
            />
          </div>

          {/* Priority */}
          <div className="cs-filter-field">
            <label className="cs-filter-label">Priority</label>
            <MultiSelect
              isMulti={true}
              options={priorityFiledOptions}
              isLoading={priorityFilterOptions.loading}
              hasMore={priorityFilterOptions.hasMore}
              placeholder="Search Priority"
              onScrollBottom={handleScrollBottom(
                priorityFilterOptions,
                getPriorityFilterOptions,
              )}
              onInputChange={handleInputChange(
                setPriorityFilterSearch,
                getPriorityFilterOptions,
              )}
              onFocus={() => {
                handleOnFocus(priorityFilterOptions, getPriorityFilterOptions);
              }}
              onMenuClose={handleResetOnBlur(
                "priorityFilterOptions",
                setPriorityFilterSearch,
              )}
              onChange={(selected) => {
                handleOnChange({
                  key: "priority",
                  selected,
                  optionsState: priorityFilterOptions,
                  fetchThunk: getPriorityFilterOptions,
                  searchResetAction: setPriorityFilterSearch,
                });
              }}
              value={priorityFiledOptions.filter((o) =>
                filtersPayload?.priority?.includes(o?.value),
              )}
            />
          </div>

          {/* Status */}
          <div className="cs-filter-field">
            <label className="cs-filter-label">Status</label>
            <MultiSelect
              isMulti={true}
              options={statusFiledOptions}
              isLoading={statusFilterOptions.loading}
              hasMore={statusFilterOptions.hasMore}
              placeholder="Search Status"
              onScrollBottom={handleScrollBottom(
                statusFilterOptions,
                getStatusFilterOptions,
              )}
              onInputChange={handleInputChange(
                setStatusFilterSearch,
                getStatusFilterOptions,
              )}
              onFocus={() => {
                handleOnFocus(statusFilterOptions, getStatusFilterOptions);
              }}
              onMenuClose={handleResetOnBlur(
                "statusFilterOptions",
                setStatusFilterSearch,
              )}
              onChange={(selected) => {
                handleOnChange({
                  key: "status",
                  selected,
                  optionsState: statusFilterOptions,
                  fetchThunk: getStatusFilterOptions,
                  searchResetAction: setStatusFilterSearch,
                });
              }}
              value={statusFiledOptions?.filter((o) =>
                filtersPayload.status?.includes(o?.value),
              )}
            />
          </div>

          {/* Health Fund Contract */}
          <div className="cs-filter-field">
            <label className="cs-filter-label">Health Fund Contract</label>
            <MultiSelect
              isMulti={true}
              options={healthFundFieldOptions}
              isLoading={healthFundContractFilterOptions.loading}
              hasMore={healthFundContractFilterOptions.hasMore}
              placeholder="Search Health Fund Contract"
              onScrollBottom={handleScrollBottom(
                healthFundContractFilterOptions,
                getHealthFundContractFilterOptions,
              )}
              onInputChange={handleInputChange(
                setHealthFundFilterContractSearch,
                getHealthFundContractFilterOptions,
              )}
              onFocus={() => {
                handleOnFocus(
                  healthFundContractFilterOptions,
                  getHealthFundContractFilterOptions,
                );
              }}
              onMenuClose={handleResetOnBlur(
                "healthFundContractFilterOptions",
                setHealthFundFilterContractSearch,
              )}
              onChange={(selected) => {
                handleOnChange({
                  key: "health_fund_contract",
                  selected,
                  optionsState: healthFundContractFilterOptions,
                  fetchThunk: getHealthFundContractFilterOptions,
                  searchResetAction: setHealthFundFilterContractSearch,
                });
              }}
              value={healthFundFieldOptions.filter((o) =>
                filtersPayload.health_fund_contract.includes(o.value),
              )}
            />
          </div>

          {/* LOS Operator */}
          <div className="cs-filter-field">
            <label className="cs-filter-label">LOS Operator</label>
            <Select
              options={losOperatorOptions}
              placeholder="Select Operator"
              value={
                filtersPayload.los_op
                  ? losOperatorOptions.find(
                      (o) => o.value === filtersPayload.los_op,
                    )
                  : ""
              }
              onChange={(selected) => {
                dispatch(
                  updateCodingSupportFilters({
                    los_op: selected?.value || "",
                    los: [],
                  }),
                );
              }}
              classNamePrefix="react-select"
            />
          </div>

          {/* Single LOS value */}
          {!["gtlt", "gtelte"].includes(filtersPayload.los_op) && (
            <div className="cs-filter-field">
              <label className="cs-filter-label">Length of Stay (LOS)</label>
              <div
                className={`${
                  !filtersPayload.los_op ? "datepicker-tooltip" : ""
                }`}
                data-title={"Select LOS operator to enable"}
              >
                <input
                  type="number"
                  min="0"
                  className="workflow-him-filter-input"
                  value={filtersPayload.los?.[0] ?? ""}
                  disabled={!filtersPayload.los_op}
                  onKeyDown={(e) => {
                    if (e.key === "e" || e.key === "E") {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    dispatch(
                      updateCodingSupportFilters({
                        los: value === "" ? [] : [Number(value)],
                      }),
                    );
                  }}
                  placeholder="Enter LOS"
                />
              </div>
            </div>
          )}

          {/* LOS Range */}
          {["gtlt", "gtelte"].includes(filtersPayload.los_op) && (
            <>
              <div className="cs-filter-field">
                <label className="cs-filter-label">LOS From</label>
                <input
                  type="number"
                  min="0"
                  className="workflow-him-filter-input"
                  value={filtersPayload.los?.[0] ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;

                    dispatch(
                      updateCodingSupportFilters({
                        los:
                          value === ""
                            ? []
                            : [Number(value), filtersPayload.los?.[1]],
                      }),
                    );
                  }}
                  placeholder="From days"
                />
              </div>

              <div className="cs-filter-field">
                <label className="cs-filter-label">LOS To</label>
                <input
                  type="number"
                  min="0"
                  className="workflow-him-filter-input"
                  value={filtersPayload.los?.[1] ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;

                    dispatch(
                      updateCodingSupportFilters({
                        los:
                          filtersPayload.los?.[0] === undefined && value === ""
                            ? []
                            : [
                                filtersPayload.los?.[0],
                                value === "" ? undefined : Number(value),
                              ],
                      }),
                    );
                  }}
                  placeholder="To days"
                />
              </div>
            </>
          )}

          <div className="cs-filter-field">
            <label className="cs-filter-label">Provisional DRG</label>

            <MultiSelect
              isMulti={true}
              options={provisionalDrgFieldOptions}
              isLoading={provisionalDrgFilterOptions.loading}
              hasMore={provisionalDrgFilterOptions.hasMore}
              placeholder="Search DRG"
              onScrollBottom={handleScrollBottom(
                provisionalDrgFilterOptions,
                getProvisionalDrgFilterOptions,
              )}
              onInputChange={handleInputChange(
                setProvisionalDrgFilterSearch,
                getProvisionalDrgFilterOptions,
              )}
              onFocus={() => {
                handleOnFocus(
                  provisionalDrgFilterOptions,
                  getProvisionalDrgFilterOptions,
                );
              }}
              onMenuClose={handleResetOnBlur(
                "provisionalDrgFilterOptions",
                setProvisionalDrgFilterSearch,
              )}
              onChange={(selected) => {
                handleOnChange({
                  key: "drg_code",
                  selected,
                  optionsState: provisionalDrgFilterOptions,
                  fetchThunk: getProvisionalDrgFilterOptions,
                  searchResetAction: setProvisionalDrgFilterSearch,
                });
              }}
              value={provisionalDrgFieldOptions.filter((o) =>
                filtersPayload.drg_code.includes(o.value),
              )}
            />
          </div>
          <div className="cs-filter-field">
            <label className="cs-filter-label">Missing Notes Type</label>
            <Select
              isMulti={true}
              options={missingNotesOptions}
              // onMenuScrollToBottom={provisionalDrgScroll}
              isLoading={provisionalDrgLoading}
              value={missingNotesOptions.filter((o) =>
                filtersPayload.reasons.includes(o.value),
              )}
              onChange={(selected) => {
                // dispatch(resetProvisionalDrg())
                dispatch(
                  updateCodingSupportFilters({
                    reasons: selected ? selected.map((s) => s.value) : [],
                  }),
                );
              }}
              classNamePrefix="react-select"
              placeholder="Search Missing Notes Type..."
            />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="cs-modal-footer">
        <Button
          variant="outline-secondary"
          onClick={onHide}
          className="el-filter-cancel btn btn-secondary"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          className="el-filter-submit btn btn-primary"
          onClick={handleApplyFilters}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CodingSupportModal;
